import { useState, useEffect } from "react";
import { Phone, Menu, X, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { label: "Fleet", href: "#fleet" },
  { label: "Ethos", href: "#ethos" },
  { label: "Service Area", href: "#areas" },
  { label: "Reserve", href: "#book" },
];

export const Header = ({ phone, payLink }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2 }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo" className="font-serif text-2xl tracking-tight text-[#F5F5F0]">
          LUXERIDE<span className="text-gold">NY</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="overline text-[#8A8A8A] hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phone}`}
            data-testid="header-call-btn"
            className="flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 hover:bg-gold hover:text-black transition-colors duration-400"
          >
            <Phone className="w-3.5 h-3.5" />
            <span className="overline text-gold hover:text-black">{phone}</span>
          </a>
          <a
            href="sms:+15162622059?&body=Hi%20LUXERIDENY%2C%20I%27d%20like%20to%20pay%20my%20fare."
            data-testid="header-pay-btn"
            className="flex items-center gap-2 bg-gold text-black px-5 py-2.5 hover:bg-gold-hover transition-colors duration-400"
          >
            <CreditCard className="w-3.5 h-3.5" />
            <span className="overline text-black">Text to Pay</span>
          </a>
        </div>

        <button
          className="md:hidden text-[#F5F5F0]"
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="overline text-[#8A8A8A] hover:text-gold"
                >
                  {l.label}
                </a>
              ))}
              <a href={`tel:${phone}`} className="overline text-gold">{phone}</a>
              <a
                href="sms:+15162622059?&body=Hi%20LUXERIDENY%2C%20I%27d%20like%20to%20pay%20my%20fare."
                data-testid="mobile-pay-btn"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 bg-gold text-black px-5 py-3 w-max"
              >
                <CreditCard className="w-4 h-4" />
                <span className="overline text-black">Text to Pay</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
